import React, { useState, useEffect, useRef } from "react";
import { Package, Star } from "lucide-react";
import "./index.css";
import ButtonComponent from "../../../../components/ButtonComponent";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  useLazyGetAllProductionPlansQuery,
  useLazyGetProductionPlanQuery,
  useUpdateActiveProductionPlanMutation,
} from "../../../../redux/services/DistributionService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import TableLoadingComponent from "../../../../components/TableLoadingComponent";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import BreadCrumbs from "../../../../components/BreadCrumbs";

interface ProductionPlanOption {
  value: string;
  label: string;
  isActive?: boolean;
}

interface ProductionPlanItem {
  itemId: string;
  displayName: string;
  productId: string;
  itemType: string;
  category: string;
  plannedQty: number;
  portionSize: string;
}

interface ProductionPlan {
  planId: string;
  displayName: string;
  isActive: boolean;
}

interface FormDataItem {
  plannedQty: number;
  producedQty: number;
}

interface FormData {
  [key: string]: FormDataItem;
}

interface RTKErrorResponse {
  status: string;
  originalStatus?: number;
  data?: any;
  error?: string;
}

const OrderManagementSystem: React.FC = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [formData, setFormData] = useState<FormData>({});
  const [selectedPlan, setSelectedPlan] = useState<ProductionPlanOption | null>(null);
  const [productionPlanOptions, setProductionPlanOptions] = useState<ProductionPlanOption[]>([]);
  const [productionItems, setProductionItems] = useState<ProductionPlanItem[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showArrow, setShowArrow] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [originalFormData, setOriginalFormData] = useState<FormData>({});
  const [starLoadingId, setStarLoadingId] = useState<string | null>(null);
  const [plansInitiallyLoaded, setPlansInitiallyLoaded] = useState(false);
  const [hasNoProductionPlans, setHasNoProductionPlans] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Refs to track current state without re-renders
  const currentRestIDRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  const [
    triggerAllPlans, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { data: productionPlans, isLoading: isLoadingPlans, error: plansError, isError: isPlansError }
  ] = useLazyGetAllProductionPlansQuery();
  
  const [
    triggerPlan,
    { isLoading: isLoadingPlan }
  ] = useLazyGetProductionPlanQuery();
  
  const [updateActiveProductionPlanMutation] = useUpdateActiveProductionPlanMutation();

  const RestID = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  // Calculate total planned quantity
  const totalPlannedQty = productionItems.reduce((total, item) => {
    return total + (item.plannedQty || 0);
  }, 0);

  // Main effect for handling restaurant changes
  useEffect(() => {
    if (!RestID) return;

    // If restaurant hasn't changed, do nothing
    if (currentRestIDRef.current === RestID) return;
    currentRestIDRef.current = RestID;

    // Reset state for new restaurant
    setSelectedPlan(null);
    setProductionItems([]);
    setFormData({});
    setOriginalFormData({});
    setHasUnsavedChanges(false);
    setProductionPlanOptions([]);
    setHasNoProductionPlans(false);
    setErrorMessage(null);
    setPlansInitiallyLoaded(false);

    // Fetch plans for the new restaurant
    fetchProductionPlans(RestID);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [RestID]);

  const fetchProductionPlans = async (restaurantId: string) => {
    if (isFetchingRef.current) return;
    
    isFetchingRef.current = true;
    try {
      const result = await triggerAllPlans(restaurantId).unwrap();
      handleProductionPlansResult(result, restaurantId);
    } catch (error) {
      handleProductionPlansError(error, restaurantId);
    } finally {
      isFetchingRef.current = false;
    }
  };

  const handleProductionPlansResult = (result: any, restaurantId: string) => {
    if (currentRestIDRef.current !== restaurantId) {
      return;
    }

    if (result && result.length > 0) {
      const options = result.map((plan: ProductionPlan) => ({
        value: plan.planId,
        label: plan.displayName,
        isActive: plan.isActive,
      }));
      
      setProductionPlanOptions(options);
      setPlansInitiallyLoaded(true);
      setHasNoProductionPlans(false);
      setErrorMessage(null);

      const activePlan = options.find((option: ProductionPlanOption) => option.isActive);
      const planToSelect = activePlan || options[0];
      
      if (planToSelect) {
        setSelectedPlan(planToSelect);
        loadProductionPlanDetails(planToSelect, restaurantId);
      }
    } else {
      setPlansInitiallyLoaded(true);
      setHasNoProductionPlans(true);
      setErrorMessage("No production plans found");
      setSelectedPlan(null);
    }
  };

  const handleProductionPlansError = (error: any, restaurantId: string) => {
    if (currentRestIDRef.current !== restaurantId) {
      return;
    }

    const typedError = error as RTKErrorResponse;
    
    if (typedError.originalStatus === 404) {
      setHasNoProductionPlans(true);
      setProductionPlanOptions([]);
      setErrorMessage("No production plans found");
    } else {
      setErrorMessage("Failed to load production plans. Please try again.");
      toast.error("Failed to load production plans");
    }
    setPlansInitiallyLoaded(true);
    setSelectedPlan(null);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setShowArrow((prev) => !prev);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

 const loadProductionPlanDetails = async (plan: ProductionPlanOption, restaurantId?: string) => {
  const targetRestaurantId = restaurantId || RestID;
  
  // Clear previous data immediately
  setProductionItems([]);
  setFormData({});
  setOriginalFormData({});

  if (!plan || !targetRestaurantId) {
    return;
  }

  try {
    const result = await triggerPlan({
      planId: plan.value,
      restaurantId: targetRestaurantId,
    }, true).unwrap(); // Force refetch
    
    if (result && result.items) {
      setProductionItems(result.items);
    }
  } catch (error) {
    console.error("Error loading plan details:", error);
    toast.error("Failed to load production plan details");
  }
};

  const handlePlanChange = async (selectedOption: ProductionPlanOption | null) => {
    if (hasUnsavedChanges) {
      const result = await Swal.fire({
        title: "Unsaved Changes",
        text: "You have unsaved changes. Changing plan will lose these changes. Continue?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, continue!",
        cancelButtonText: "Cancel",
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    setSelectedPlan(selectedOption);
    if (selectedOption) {
      await loadProductionPlanDetails(selectedOption);
    } else {
      setProductionItems([]);
      setFormData({});
      setOriginalFormData({});
      setHasUnsavedChanges(false);
    }
  };

  const handleStarClick = async (plan: ProductionPlanOption) => {
    if (!RestID || plan.isActive || currentRestIDRef.current !== RestID) {
      return;
    }

    try {
      setStarLoadingId(plan.value);
      
      await updateActiveProductionPlanMutation({
        restaurantId: RestID,
        planId: plan.value,
        isActive: true,
      }).unwrap();

      toast.success(`"${plan.label}" is now the active production plan!`);
      
      // Refetch plans to get updated active status
      await fetchProductionPlans(RestID);
      
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to update active plan");
    } finally {
      setStarLoadingId(null);
    }
  };

  const handleEditPlan = () => {
    if (selectedPlan && currentRestIDRef.current === RestID) {
      navigate("/distribution/orderManagement/editProductionPlan", {
        state: { 
          planId: selectedPlan.value, 
          planName: selectedPlan.label,
          planData: {
            displayName: selectedPlan.label,
            items: productionItems,
            isActive: selectedPlan.isActive,
          }
        },
      });
    }
  };

  // Add cleanup on unmount
  useEffect(() => {
    return () => {
      currentRestIDRef.current = null;
      isFetchingRef.current = false;
    };
  }, []);

  return (
    <>
      <BreadCrumbs hasSubMenus={true}/>
      <div className="production-management-container">
        <div className="production-management-header">
          <div className="production-plan-title-container">
            <Package className="production-management-icon" />
            {selectedPlan ? (
              <div className="title-and-actions-container">
                <div className="title-and-star-container">
                  <h1 className="production-management-title">{selectedPlan.label}</h1>
                  {starLoadingId === selectedPlan.value ? (
                    <Spinner className="star-loading-spinner" />
                  ) : (
                    <Star
                      className={`favorite-star-icon ${
                        selectedPlan.isActive ? "active" : ""
                      }`}
                      size={16}
                      fill={selectedPlan.isActive ? "currentColor" : "none"}
                      style={{ cursor: selectedPlan.isActive ? "default" : "pointer" }}
                      onClick={() => !selectedPlan.isActive && handleStarClick(selectedPlan)}
                    />
                  )}
                </div>
              </div>
            ) : (
              <h2 className="production-management-title">
                {hasNoProductionPlans ? "No Production Plans Available" : "Select a Production Plan"}
              </h2>
            )}
          </div>
          <div className="production-controls-section">
            <div className="production-plan-controls-container">
              <div className="production-plan-header-controls">
                <div className="production-plan-dropdown">
                  <Select
                    value={selectedPlan}
                    onChange={handlePlanChange}
                    options={productionPlanOptions}
                    placeholder={
                      isLoadingPlans
                        ? "Loading plans..."
                        : hasNoProductionPlans 
                        ? "No plans available" 
                        : "Select Production Plan"
                    }
                    isSearchable
                    isLoading={isLoadingPlans}
                    isDisabled={isLoadingPlans || hasNoProductionPlans}
                  />
                </div>
                <div className="production-plan-button-container">
                  <ButtonComponent
                    onClick={() => navigate("/distribution/orderManagement/addProductionPlan")}
                    titel="Add"
                  />
                </div>
                {selectedPlan && (
                  <div className="production-plan-button-container">
                    <ButtonComponent
                      onClick={handleEditPlan}
                      titel="Edit"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      
        <div className="production-table-wrapper">
          {isLoadingPlan ? (
            <TableLoadingComponent numColumns={5} />
          ) : productionItems.length === 0 ? (
            <div className="empty-state-container">
              {hasNoProductionPlans && plansInitiallyLoaded ? (
                <div className="no-production-plans-container">
                  <Package size={64} className="text-gray-400 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    No Production Plans Found
                  </h3>
                  <p className="text-gray-500 mb-6">
                    You don't have any production plans yet. Create your first one to get started.
                  </p>
                  <ButtonComponent
                    onClick={() => navigate("/distribution/orderManagement/addProductionPlan")}
                    titel="Create Production Plan"
                  />
                </div>
              ) : selectedPlan && !isLoadingPlan ? (
                <div>
                <TableLoadingComponent numColumns={5}/> 
                </div>
              ) : null}
            </div>
          ) : (
            <table className="production-items-table">
              <thead>
                <tr>
                  <th>SKU ID</th>
                  <th>Product Name</th>
                  <th>Portion Size</th>
                  <th>Category</th>
                  <th>Planned Qty(Packs)</th>
                </tr>
              </thead>
              <tbody>
                {productionItems.map((item) => (
                  <tr key={item.itemId}>
                    <td className="sku-id-cell">{item.productId}</td>
                    <td>{item.displayName}</td>
                    <td>{item.portionSize}</td>
                    <td>{item.category}</td>
                    <td>{item.plannedQty}</td>
                  </tr>
                ))}
                {/* Total Quantity Row */}
                <tr className="total-quantity-row">
                  <td colSpan={4} className="total-label-cell">
                    <strong>Total Planned Quantity</strong>
                  </td>
                  <td className="total-quantity-cell">
                    <strong>{totalPlannedQty.toLocaleString()}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderManagementSystem;